import { type ComputeFragment, type ComputeFragmentRect } from "./powerTreemapLayout"

// ─────────────────────────────────────────────────────────────────────────────
// Stable squarified treemap — from first principles, jitter-free AND square.
//
// A binary treemap keeps cells stable but can only give a small-value cell a
// thin sliver (a single cut across a big rect). Squarify fixes the shape by
// packing cells into ROWS, but re-squarifying every frame makes cells hop
// between rows as values drift (jitter). The synthesis:
//
// P1. Cells are packed into ROWS (squarify) → cells are close to square.
// P2. The rows PERSIST: a cell stays in its row across steps. Rows only change
//     by appending a NEW cell (to the row where it best fits, else a new row) or
//     removing a departed one. Existing cells never regroup → no jitter/flips.
// P3. The row orientation (which way rows stack) is frozen per region → no flips.
// P4. Every frame only re-reads values and re-derives rectangles from the fixed
//     rows by proportional splits → overlap-free and jitter-free by construction.
// P5. New cells enter at value 0 (0 area → invisible) and grow; departed cells
//     shrink to 0 then are pruned → smooth appearance/disappearance.
// P6. Regions (US/CH/RoW) are their own frozen row layout → countries stay put.
//
// State persists across renders (module-level; one live treemap at a time).
// ─────────────────────────────────────────────────────────────────────────────

type Rect = { x: number; y: number; width: number; height: number }
// `vertical`: rows are vertical strips stacked left→right (used when the region
// is wider than tall). Otherwise rows are horizontal strips stacked top→down.
type RowState = { vertical: boolean; rows: string[][] }

const rowCache = new Map<string, RowState>()
export const resetStableTreemap = (): void => { rowCache.clear() }

// Snapshot / restore the persistent row state, so the target-prune can run the
// REAL layout to estimate cell sizes (a dry run) without permanently mutating
// the animation's rows.
export type RowCacheSnapshot = Map<string, RowState>
export const snapshotRowCache = (): RowCacheSnapshot => {
  const snap: RowCacheSnapshot = new Map()
  for (const [k, v] of rowCache) snap.set(k, { vertical: v.vertical, rows: v.rows.map(r => [...r]) })
  return snap
}
export const restoreRowCache = (snap: RowCacheSnapshot): void => {
  rowCache.clear()
  for (const [k, v] of snap) rowCache.set(k, { vertical: v.vertical, rows: v.rows.map(r => [...r]) })
}

// Worst (largest) aspect ratio in a row of `areas` laid along a strip of length
// `side`; zero-area cells (growing/shrinking through 0) are ignored so they
// don't drive grouping.
const worstAspect = (areas: number[], side: number): number => {
  let sum = 0, max = 0, min = Infinity
  for (const a of areas) { if (a <= 0) continue; sum += a; if (a > max) max = a; if (a < min) min = a }
  if (sum <= 0 || side <= 0 || !isFinite(min)) return 1
  const s2 = sum * sum, side2 = side * side
  return Math.max((side2 * max) / s2, s2 / (side2 * min))
}

const sumOf = (ids: string[], valueOf: Map<string, number>): number => {
  let s = 0
  for (const id of ids) s += Math.max(0, valueOf.get(id) ?? 0)
  return s
}

// Reconcile the persistent rows for `key` to exactly `ids` (P2): drop departed
// cells (P5 handles their fade via value→0 before this prunes them), then append
// each newcomer to the row where it keeps the best aspect, or a fresh row.
// Existing cells keep their row. Orientation is decided once and frozen (P3).
const reconcileRows = (key: string, ids: string[], valueOf: Map<string, number>, rect: Rect): RowState => {
  let st = rowCache.get(key)
  if (!st) st = { vertical: rect.width >= rect.height, rows: [] }
  const want = new Set(ids)
  st.rows = st.rows.map(row => row.filter(id => want.has(id))).filter(row => row.length > 0)
  const have = new Set<string>()
  for (const row of st.rows) for (const id of row) have.add(id)

  const side = st.vertical ? rect.height : rect.width
  const totalV = sumOf(ids, valueOf) || 1
  const areaScale = (rect.width * rect.height) / totalV
  for (const id of ids) {
    if (have.has(id)) continue
    const area = Math.max(0, valueOf.get(id) ?? 0) * areaScale
    const lastRow = st.rows[st.rows.length - 1]
    if (!lastRow) { st.rows.push([id]) }
    else {
      const lastAreas = lastRow.map(rid => Math.max(0, valueOf.get(rid) ?? 0) * areaScale)
      if (worstAspect([...lastAreas, area], side) <= worstAspect(lastAreas, side)) lastRow.push(id)
      else st.rows.push([id])
    }
    have.add(id)
  }
  rowCache.set(key, st)
  return st
}

// P4: derive rects from the fixed rows by proportional splits.
const placeRows = (st: RowState, valueOf: Map<string, number>, rect: Rect, out: Map<string, Rect>): void => {
  const V = sumOf(st.rows.flat(), valueOf) || 1
  if (st.vertical) {
    let cx = rect.x
    for (const row of st.rows) {
      const Vi = sumOf(row, valueOf)
      const rowW = (Vi / V) * rect.width
      let cy = rect.y
      for (const id of row) {
        const v = Math.max(0, valueOf.get(id) ?? 0)
        const cellH = Vi > 0 ? (v / Vi) * rect.height : 0
        out.set(id, { x: cx, y: cy, width: rowW, height: cellH })
        cy += cellH
      }
      cx += rowW
    }
  } else {
    let cy = rect.y
    for (const row of st.rows) {
      const Vi = sumOf(row, valueOf)
      const rowH = (Vi / V) * rect.height
      let cx = rect.x
      for (const id of row) {
        const v = Math.max(0, valueOf.get(id) ?? 0)
        const cellW = Vi > 0 ? (v / Vi) * rect.width : 0
        out.set(id, { x: cx, y: cy, width: cellW, height: rowH })
        cx += cellW
      }
      cy += rowH
    }
  }
}

const REGION_ORDER: Record<string, number> = { america: 0, china: 1, world: 2 }

export const layoutComputeFragmentsStable = (
  fragments: ComputeFragment[],
  x: number,
  y: number,
  width: number,
  height: number,
): ComputeFragmentRect[] => {
  if (fragments.length === 0 || width <= 0 || height <= 0) return []
  const order: string[] = []
  const groups = new Map<string, ComputeFragment[]>()
  for (const f of fragments) {
    const r = f.lab.regionId
    if (!groups.has(r)) { groups.set(r, []); order.push(r) }
    groups.get(r)!.push(f)
  }
  order.sort((a, b) => (REGION_ORDER[a] ?? 99) - (REGION_ORDER[b] ?? 99))

  // P6: region-level rows (regions sized by total compute).
  const regionValue = new Map<string, number>()
  for (const r of order) regionValue.set(r, sumOf(groups.get(r)!.map(f => f.id), new Map(groups.get(r)!.map(f => [f.id, Math.max(0, f.value)]))))
  const canvas: Rect = { x, y, width, height }
  const regionSt = reconcileRows("regions", order, regionValue, canvas)
  const regionRects = new Map<string, Rect>()
  placeRows(regionSt, regionValue, canvas, regionRects)

  const out: ComputeFragmentRect[] = []
  for (const r of order) {
    const frags = groups.get(r)!
    const rr = regionRects.get(r)
    if (!rr) continue
    const ids = frags.map(f => f.id)
    const valueOf = new Map(frags.map(f => [f.id, Math.max(0, f.value)]))
    const fragOf = new Map(frags.map(f => [f.id, f]))
    const st = reconcileRows(`cells:${r}`, ids, valueOf, rr)
    const rects = new Map<string, Rect>()
    placeRows(st, valueOf, rr, rects)
    for (const id of ids) {
      const cr = rects.get(id)
      const frag = fragOf.get(id)
      if (cr && frag) out.push({ fragment: frag, x: cr.x, y: cr.y, width: cr.width, height: cr.height })
    }
  }
  return out
}
